import { ImageSources } from '@/components/Image/ArtDirectedImage';
import { ButtonContent } from '@/components/OldButton/OldButton';
import { VimeoVideoType } from '@/components/VimeoVideo/VimeoVideo';
import {
  Contentful,
  FeaturedComponent,
  FrontifyImageContent,
  IconNameContent,
} from '@/contentful';
import { ContentStyle } from '@/contentful/types';
import useViewPortSize from '@/hooks/useViewportSize';
import { ViewportSize } from '@/styles/viewportSize';
import { getBoundedAspectRatio } from '@/utils/media';

import HighlightMediaTextFiftyFifty from './HighlightMediaTextFiftyFifty/HighlightMediaTextFiftyFifty';
import HighlightMediaTextFull, {
  HighlightMediaTextProps,
} from './HighlightMediaTextFull/HighlightMediaTextFull';
import { TextContentAppLinks } from './TextContent/TextContent';

export interface HighlightIconText extends Contentful {
  fields: {
    name: string;
    icon?: IconNameContent;
    text?: string;
  };
}

export interface HighlightImageTextContent extends Contentful {
  fields: {
    variant: string;
    style?: ContentStyle;
    textAlignment?: string;
    eyebrow?: string;
    headline?: string;
    subhead?: string;
    images?: FrontifyImageContent[];
    inferImageAspectRatioOnMobile?: boolean;
    features?: HighlightIconText[];
    cta?: ButtonContent[];
    isHeroMode?: boolean;
    video?: VimeoVideoType;
    imageAltText?: string;
    appLinks?: TextContentAppLinks;
  };
}

export type HighlightImageTextProps = FeaturedComponent<{
  content: HighlightImageTextContent;
}>;

const getImageAspectRatio = (images: FrontifyImageContent[] | undefined) => {
  if (!images || images.length < 1) return `1 / 1`;
  const [primaryImage, mobileOnlyImage] = images;

  if (mobileOnlyImage) {
    return getBoundedAspectRatio(mobileOnlyImage.width, mobileOnlyImage.height);
  }

  return getBoundedAspectRatio(primaryImage.width, primaryImage.height);
};

export default function HighlightMediaText(props: HighlightImageTextProps) {
  const {
    isFeatured,
    content: {
      fields: {
        variant,
        style,
        textAlignment,
        eyebrow,
        headline,
        images,
        inferImageAspectRatioOnMobile,
        subhead,
        features,
        cta,
        isHeroMode,
        video,
        imageAltText,
        appLinks,
      },
    },
  } = props;
  const isOnDark = style === ContentStyle.OnDark;
  const { isExtraSmall, isSmall } = useViewPortSize();
  const isMobile = isExtraSmall || isSmall;
  const isFull = variant === 'Full';
  const isRightAligned = textAlignment === 'Right' && !isMobile;

  const mobileImageAspectRatio = inferImageAspectRatioOnMobile
    ? getImageAspectRatio(images)
    : undefined;

  const castedImages = images as unknown as FrontifyImageContent[];

  const imageSources: ImageSources = {
    xs: [castedImages?.[1], ViewportSize.sm],
    sm: [castedImages?.[1], ViewportSize.md],
    md: [castedImages?.[0], ViewportSize.lg],
    lg: [castedImages?.[0], ViewportSize.xl],
    xl: [castedImages?.[0], ViewportSize.xl],
    fallback: [castedImages?.[0], ViewportSize.md],
  };

  const highlightMediaTextProps: HighlightMediaTextProps = {
    isMobile,
    imageSources,
    imageAltText,
    mobileImageAspectRatio,
    video,
    isOnDark,
    isRightAligned,
    subhead,
    cta,
    features,
    isHeroMode,
    eyebrow,
    headline,
  };

  return isFull ? (
    <HighlightMediaTextFull {...highlightMediaTextProps} />
  ) : (
    <HighlightMediaTextFiftyFifty
      {...highlightMediaTextProps}
      isFeatured={isFeatured}
      appLinks={!isHeroMode ? appLinks : undefined}
    />
  );
}
